export interface VadResult {
  readonly isSpeechActive: boolean;
  readonly speechDurationMs: number;
  readonly silenceDurationMs: number;
  readonly rmsLevel: number;
  readonly noiseFloor: number;
}

export interface VadConfig {
  /** Speech activation threshold multiplier above noise floor (default 3.0) */
  activationMultiplier?: number;
  /** Speech deactivation threshold multiplier (hysteresis, default 1.8) */
  deactivationMultiplier?: number;
  /** Minimum speech activation energy (default 0.015) */
  minEnergyThreshold?: number;
}

export class EnergyVad {
  private noiseFloor = 0.008;
  private isSpeechActive = false;
  private speechDurationMs = 0;
  private silenceDurationMs = 0;

  private readonly activationMultiplier: number;
  private readonly deactivationMultiplier: number;
  private readonly minEnergyThreshold: number;

  constructor(config: VadConfig = {}) {
    this.activationMultiplier = config.activationMultiplier ?? 3.0;
    this.deactivationMultiplier = config.deactivationMultiplier ?? 1.8;
    this.minEnergyThreshold = config.minEnergyThreshold ?? 0.015;
  }

  /**
   * Processes an incoming audio chunk (e.g. 32ms @ 16kHz)
   */
  process(chunk: Float32Array, chunkDurationMs: number): VadResult {
    const len = chunk.length;
    if (len === 0) {
      return {
        isSpeechActive: this.isSpeechActive,
        speechDurationMs: this.speechDurationMs,
        silenceDurationMs: this.silenceDurationMs,
        rmsLevel: 0,
        noiseFloor: this.noiseFloor,
      };
    }

    // 1. Calculate Root Mean Square (RMS) energy
    let sumSq = 0;
    for (let i = 0; i < len; i++) {
      const s = chunk[i]!;
      sumSq += s * s;
    }
    const rms = Math.sqrt(sumSq / len);

    // 2. Adaptive noise floor tracking (slow upward adaptation, fast downward tracking)
    if (!this.isSpeechActive) {
      if (rms < this.noiseFloor) {
        this.noiseFloor = this.noiseFloor * 0.9 + rms * 0.1;
      } else {
        this.noiseFloor = this.noiseFloor * 0.995 + rms * 0.005;
      }
    }
    this.noiseFloor = Math.max(0.001, Math.min(0.1, this.noiseFloor));

    // 3. Thresholds with hysteresis
    const startThreshold = Math.max(this.minEnergyThreshold, this.noiseFloor * this.activationMultiplier);
    const stopThreshold = Math.max(this.minEnergyThreshold * 0.7, this.noiseFloor * this.deactivationMultiplier);

    // 4. State transition
    if (!this.isSpeechActive) {
      if (rms >= startThreshold) {
        this.isSpeechActive = true;
        this.speechDurationMs = chunkDurationMs;
        this.silenceDurationMs = 0;
      } else {
        this.silenceDurationMs += chunkDurationMs;
      }
    } else {
      if (rms < stopThreshold) {
        this.silenceDurationMs += chunkDurationMs;
        // Require 300ms of consecutive sub-threshold audio to deactivate speech
        if (this.silenceDurationMs >= 300) {
          this.isSpeechActive = false;
          this.speechDurationMs = 0;
        }
      } else {
        this.speechDurationMs += chunkDurationMs;
        this.silenceDurationMs = 0;
      }
    }

    return {
      isSpeechActive: this.isSpeechActive,
      speechDurationMs: this.speechDurationMs,
      silenceDurationMs: this.silenceDurationMs,
      rmsLevel: rms,
      noiseFloor: this.noiseFloor,
    };
  }

  reset(): void {
    this.isSpeechActive = false;
    this.speechDurationMs = 0;
    this.silenceDurationMs = 0;
    this.noiseFloor = 0.008;
  }
}
